<template>
  <section id="login">
    <div class="text-center mb-3">
      <img
        alt
        :src="$storeIndex.getImageGobalAssets('authorization/logo-goship-gosell')"
        class="logo-goship-gosell"
      >
    </div>
    <form id="LoginForm" data-test="login-form">
      <div
        v-if="message"
        class="form-group"
        data-test="message"
      >
        <div class="alert alert-danger">
          <i class="fa fa-info-circle" /> {{ $t(message) }}
        </div>
      </div>
      <div
        class="form-group form-group-feedback form-group-feedback-left"
        :class="{'form-group-error': $v.form.username.$error}"
      >
        <input
          type="text"
          class="form-control"
          :placeholder="$t('Username')"
          @keyup.enter="handleLogin"
          v-model.trim.lazy="$v.form.username.$model"
          data-test="input-username"
        >
        <div class="form-control-feedback">
          <i class="fa-user-check far text-muted" />
        </div>
        <div
          class="validation-invalid-label"
          v-if="!$v.form.username.required"
          data-test="validation-username"
        >
          {{ $t('Please enter username.') }}
        </div>
        <div
          class="validation-invalid-label"
          v-if="!$v.form.username.email"
          data-test="validation-username"
        >
          {{ $t('Email invalid format.') }}
        </div>
      </div>
      <div
        class="form-group form-group-feedback form-group-feedback-left"
        :class="{'form-group-error': $v.form.password.$error}"
      >
        <input
          :type="showPasswordType"
          class="form-control"
          :placeholder="$t('Password')"
          @keyup.enter="handleLogin"
          v-model.trim.lazy="$v.form.password.$model"
          data-test="input-password"
        >
        <div class="form-control-feedback">
          <i class="fa-user-lock far text-muted" />
        </div>
        <span
          class="form-control-feedback"
          style="margin-left: 87%;"
        >
          <a
            class="cursor-pointer"
            @click="showPassword"
          ><i :class="iconShowPassword" /></a>
        </span>
        <div
          class="validation-invalid-label"
          v-if="!$v.form.password.required"
          data-test="validation-password"
        >
          {{ $t('Please enter password.') }}
        </div>
        <div
          v-else-if="!$v.form.password.minLength"
          class="validation-invalid-label"
          data-test="validation-password"
        >
          {{ $t('Password must have at least') + ' ' + $v.form.password.$params.minLength.min + ' ' + $t('letters.') }}
        </div>
      </div>
      <div
        class="justify-content-center mt-0 mx-4 mb-3"
        data-test="vue-recaptcha"
      >
        <vue-recaptcha
          ref="recaptcha"
          @verify="onRecaptchaVerify"
          :load-recaptcha-script="true"
          :sitekey="siteKey"
          :model-value="responseVerify"
          @expired="onRecaptchaExpired"
          @error="onRecaptchaError"
        />
        <span
          v-show="responseVerify == '' && alertVerifyCaptcha && !$storeIndex.getCheckCy()"
          class="text-danger"
          data-test="validation-recaptcha"
        >
          *{{ $t("Verify that you are not robot.") }}
        </span>
      </div>
      <div class="form-group">
        <div class="row">
          <div class="col-6">
            <div class="form-check">
              <label class="form-check-label">
                <div class="border-primary text-primary uniform-checker" data-test="input-remember">
                  <span :class="{'checked': rememberMe }">
                    <input
                      v-model="rememberMe"
                      @keyup.enter="handleLogin"
                      type="checkbox"
                      class="form-check-input-styled"
                    >
                  </span>
                </div>{{ $t('Remember') }}
              </label>
            </div>
          </div>
          <div class="col-6">
            <div class="text-right">
              <router-link
                :to="{ name: 'auth.forgotpassword' }"
                class="ml-auto"
                data-test="link-forgotpassword"
              >
                {{ $t('Forgot password?') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
      <div class="form-group">
        <ladda
          text="Sign in"
          :btn-class="'btn btn-primary btn-block'"
          :loading="loading"
          icon="<i class='fa fa-sign-in-alt'></i>"
          data-test="btn-login"
          @click="handleLogin()"
        />
      </div>
    </form>
    <div v-if="!sso">
      <div class="form-group text-center text-muted content-divider">
        <span class="px-2">{{ $t("or sign in with") }}</span>
      </div>

      <div class="form-group">
        <div class="d-flex justify-content-around">
          <button
            type="button"
            @click="handleSocialLogin('google')"
            class="btn bg-google btn-block my-0 text-indigo-400 d-flex justify-content-center align-items-center"
            data-test="btn-signin-google"
          >
            <i class="fa-brands fa-google f-18" /> 
          </button>
          <button
            type="button"
            @click="handleSocialLogin('facebook')"
            class="btn bg-facebook btn-block my-0 mx-2 text-indigo-400 d-flex justify-content-center align-items-center"
            data-test="btn-signin-facebook"
          >
            <i class="fa-brands fa-facebook-f f-18" />
          </button>
          <button
            type="button"
            @click="handleSocialLogin('line')"
            class="btn bg-line btn-block my-0 text-indigo-400 d-flex justify-content-center align-items-center"
            data-test="btn-signin-line"
          >
            <i class="fa-brands fa-line f-18" /> 
          </button>
          <button
            type="button"
            @click="handleSocialLogin('apple')"
            class="btn bg-apple btn-block my-0 ml-2 text-indigo-400 d-flex justify-content-center align-items-center"
            data-test="btn-signin-apple"
          >
            <i class="fa-brands fa-apple f-18" />
          </button>
        </div>
      </div>
      <div class="form-group text-center text-muted content-divider">
        <span class="px-2">{{ $t("Don't have an account") }}?</span>
      </div>
      <div class="form-group">
        <router-link
          :to="{ name: 'auth.register' }"
          class="btn btn-light btn-block"
          data-test="btn-register"
        >
          {{ $t('Sign up') }}
        </router-link>
      </div>
      <div class="form-group mb-0">
        <div class="btn-group-justified">
          <ul
            class="m-auto navbar-nav"
            style="width: 125px;"
          >
            <li class="nav-item dropdown language-switch">
              <a
                class="navbar-nav-link dropdown-toggle"
                data-toggle="dropdown"
              >
                <img
                  :src="$storeIndex.getImageSrc('lang/' + lang)"
                  class="img-flag mr-2"
                  alt=""
                > {{ $t(title) }}
              </a>
              <div class="dropdown-menu">
                <a
                  v-for="value in localList"
                  :name="value.code"
                  :key="`lang-${value.code}`"
                  @click="selectLang(value.code)"
                  href="javascript:void(0);"
                  class="dropdown-item english"
                >
                  <img
                    :src="$storeIndex.getImageSrc('lang/' + value.code)"
                    class="img-flag"
                    alt=""
                  > {{ $t(value.name) }}
                </a>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
div.validation-invalid-label { display: none !important; }
div.form-group-error>div.validation-invalid-label { display: block !important; }
</style>

<script>
import Cookies from 'js-cookie'
import { required, email, minLength } from "vuelidate/lib/validators";
import Ladda from '@/plugins/Ladda.vue'
import CentralApi from '@/services/endpoint/central.service'
window.Pusher = require('pusher-js');
import {
    localSave,
    localRead
} from "@/store/lang";
import { v4 as uuid } from 'uuid';
import WebSocket from "isomorphic-ws";
import languages from "@/plugins/static/language.json";
import VueRecaptcha from 'vue-recaptcha';

export default {
    name : "LogIn",
    props: {
        sso: {
            type: Boolean,
            default: false
        },
        redirect: {
            type: String,
            default: ''
        }
    },
    components: {
        Ladda,
        'vue-recaptcha': VueRecaptcha
    },
    data() {
        return {
            rememberMe: false,
            lang: localRead("local"),
            localList: languages,
            form: {
                username: '',
                password: ''
            },
            loading: false,
            messag: '',
            message: '',
            submitStatus: false,
            showPasswordType: 'password',
            iconShowPassword: 'far fa-eye-slash',
            allPages : ['store', 'general', 'referfriends', 'security'],
            siteKey: '6Ldz_rcqAAAAANC72R7LwjMbU014QigbrVURnI_0',
            responseVerify: '',
            alertVerifyCaptcha: false,
        };
    },
    computed: {
        title() {
          return this.localList.find(x => x.code === this.lang).name
        },
        showCaptcha () {
          if (this.form.username != '' && this.form.password != '') return true
          else return false
        }
    },
    validationConfig: {
        $lazy: true,
    },
    validations: {
        form: {
            username: {
                required,
                email,
            },
            password: {
                required,
                minLength: minLength(8)
            },
            recaptcha: {
              responseVerify() {
                return this.responseVerify ? true : false;
              }
            }
        },
        validationGroup: ['form.username', 'form.password', 'form.recaptcha']
    },
    created() {
    },
    methods: {
        handleSocialLogin: async function(provider) {
            if (!this.responseVerify) {
              this.alertVerifyCaptcha = true;
              return;
            } else {
              this.alertVerifyCaptcha = false;
            }
            if((provider === 'facebook' || provider === 'apple') && process.env.NODE_ENV === 'local') return ;
            var lastConnection = new Date().getTime()
            var everySeconds = (58 * 1000)
            var plushSeconds = (56 * 1000)
            Cookies.set('loginBrowser', 'gosaasApp')

            const uuidV4 = uuid.v4()
            var clientUid = provider == 'apple' ? `socialAppleLoginChannel${uuidV4}` : `socialLoginChannel${uuidV4}`
            const ws = new WebSocket(process.env.WEBSOCKETS_GOQUEUE_URL)
            ws.onopen = function open() {
              ws.send(JSON.stringify({
                data: {
                  clientUid: clientUid
                },
                event : "subscribe"
              }));
              setInterval(() => {
                if(lastConnection < new Date().getTime() && ws.readyState === 1) {
                  ws.send(JSON.stringify({
                    data: {
                      clientUid: clientUid
                    },
                    event: `pusher:ping`
                  }))
                }
              }, everySeconds);
            };
            //start websockets

            let socialLogin = window.open(process.env.BASEURL + `/v1/auth/watiting`);

            if (socialLogin) {
              setTimeout(() => {
                socialLogin.location.href = process.env.API_BASE_URL + `/v1/auth/${provider}/login?code=${uuidV4}`;
              }, 3000);
            }

            if(provider == 'apple') {
                Cookies.set('closeAppleAuthTab', 'false')
                //web sockets listen
                ws.onmessage = async (e) => {
                  lastConnection = new Date().getTime() + plushSeconds
                  const {data} = JSON.parse(e.data)

                  if(data.message === 'done') {
                    try {
                      const queryString = `id=${window.btoa(data.data)}&action_type=login`
                      const response = await CentralApi.Auth.socialAppleActions(queryString)
                      const reponseMessage = response.data.message 
                      const responseData = response.data.data

                      if(reponseMessage === 'toRegister') {
                          const obj = {
                              id : responseData,
                              provider 
                          }
                          const queryString = new URLSearchParams(obj).toString()
                          const encrypt = this.$CryptoJS.AES.encrypt(queryString, process.env.NODE_ENV).toString()
                          const base64 = window.btoa(encrypt);
                          Cookies.set('closeAppleAuthTab', 'true') 
                          window.location.href = '/v1/auth/apple/callback?code=' + base64
                      }

                      if(reponseMessage === 'toLogin') {
                          Cookies.set('centralToken', responseData.access_token, { domain: '.' + process.env.CENTRAL_API_ROOT })
                          Cookies.set('closeAppleAuthTab', 'true') 
                          location.reload();
                      }
                    }
                    catch(err) {
                      Cookies.set('closeAppleAuthTab', 'true') ;
                      this.$swal.fire({
                        title: this.$t("Something went wrong."),
                        icon: "error",
                        html: this.$t("Something Wrong, Please Try Again."),
                        confirmButtonText: this.$t("OK"),
                        buttonsStyling: false,
                        customClass: {
                          confirmButton: "btn btn-primary",
                          cancelButton: "btn btn-light",
                        },
                        focusConfirm: false,
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                      })
                    }
                  }

                  if(data.message === 'cancelProcess') Cookies.set('closeAppleAuthTab', 'true')
                }
                
            } else {
                //web sockets listen
                ws.onmessage = async (e) => {
                  lastConnection = new Date().getTime() + plushSeconds
                  const {data} = JSON.parse(e.data)
                  
                  if(data.message === 'done'){
                    const interval = setInterval(()=>{
                      if(Cookies.get('centralToken')){
                          clearInterval(interval)

                          if(this.$route.query.redirect && this.$route.query.redirect != ''){
                              const checkHasPage = this.checkHasPage(this.$route.query.redirect);
                              
                              if(checkHasPage){
                                  window.location.href = process.env.CENTRAL_ACC_URL + '/' + this.$route.query.redirect
                              }else{
                                  location.reload();
                              }
                          } else {
                              location.reload();
                          }
                      }
                    },1000);
                  }
                }
            }
        },
        handleLogin: function() {
            this.$v.$touch()
            if(this.$storeIndex.getCheckCy()){
              this.responseVerify = true
            }
            if (!this.responseVerify) {
              this.alertVerifyCaptcha = true;
              return;
            } else {
              this.alertVerifyCaptcha = false;
            }
            if(!this.$v.$invalid && this.responseVerify) {
                this.loading = true;
                this.$store.dispatch('auth/login', {
                    username: this.$v.form.username.$model,
                    password: this.$v.form.password.$model,
                    remember: this.rememberMe
                }).then((res) => {
                    this.loading = false;
                    if('message' in res) {

                      if("code" in res && res.code === "ERROR_PASSWORD_EXPIRED") {
                        window.location.href = process.env.CENTRAL_APP_URL+'/changepassword?v='+res.email+'&si='+btoa(this.$v.form.username.$model)+'&rm='+this.rememberMe+'&redirect='+this.redirect;
                        return
                      }

                      this.message = res.message
                      return
                    }

                    if(this.sso) {
                        window.location.href = window.location.origin + '/sso-success?redirect=' + this.redirect;
                    } else {
                        if(this.redirect != ''){
                            const checkHasPage = this.checkHasPage(this.redirect);
                            if(checkHasPage){
                                window.location.href = process.env.CENTRAL_ACC_URL + '/' + this.redirect;
                            }else{
                                window.location.href = process.env.CENTRAL_ACC_URL + '/store';
                            }
                        } else {
                            window.location.href = process.env.CENTRAL_ACC_URL + '/store';
                        }
                    }
                },
                error => {
                    this.message = error.response.data.message;
                    this.loading = false;
                })
            }
        },
        selectLang(lang) {
            localSave("local", lang);
            location.reload();
        },
        async showPassword() {
            this.showPasswordType = this.showPasswordType === 'password' ? 'text': 'password';
            this.iconShowPassword = this.iconShowPassword === 'far fa-eye-slash'? 'far fa-eye': 'far fa-eye-slash';
        },
        checkHasPage(page) {
            return this.allPages.includes(page);
        },
        onRecaptchaVerify: function (response) {
          this.responseVerify = response
          if (this.responseVerify) {
            this.submitStatus = true;
            this.alertVerifyCaptcha = false;
            // this.handleLogin()
          }
        },
        resetRecaptcha() {
          this.$refs.recaptcha.reset();
          this.alertVerifyCaptcha = false;
        },
        onRecaptchaExpired() {
          this.responseVerify = '';
          this.alertVerifyCaptcha = true;
        },
        onRecaptchaError() {
          this.responseVerify = '';
          this.alertVerifyCaptcha = true;
        }
    },
    mounted() {
      this.resetRecaptcha()
        if (this.$cookie.get('login_email') !== undefined) {
            this.rememberMe = true;
            this.$v.form.username.$model = this.$cookie.get('login_email');
            this.$v.form.password.$model = this.$storeIndex.b64DecodeUnicode(this.$cookie.get('login_password'));
        }

        const getAllCookies = () =>  document.cookie.split(';').reduce((ac, str) => Object.assign(ac, {[str.split('=')[0].trim()]: str.split('=')[1]}), {});

        const except = ['local', 'login_email', 'login_password'];

        for (const cookie in getAllCookies()) {
          if(!except.includes(cookie)) {
            this.$cookie.remove(cookie)
            this.$cookie.remove(cookie, { domain: '.' + process.env.CENTRAL_API_ROOT });
          }
        }
    },
};
</script>
<style scoped>
    input[type="email"],
    input[type="text"],
    input[type="password"] {
        font-family: 'Sarabun', sans-serif;
    }

    .f-18{
        font-size: 18px;
    }
</style>



// WEBPACK FOOTER //
// src/components/central/authorization/Login.vue